import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Turkish-style date input (dd/MM/yyyy) that stores/emits ISO yyyy-MM-dd.
 * - Text field with an auto-slash mask (digits only, max 10 chars) and real-calendar validation.
 * - A calendar button opens the browser's native picker through a visually hidden <input type="date">.
 * - Emits '' when cleared or when the typed value is incomplete/invalid on blur.
 */
export interface DateInputProps {
  value: string
  onChange: (iso: string) => void
  min?: string
  max?: string
  required?: boolean
  disabled?: boolean
  placeholder?: string
  'aria-label'?: string
  id?: string
  name?: string
  className?: string
  style?: CSSProperties
}

const pad2 = (n: number) => String(n).padStart(2, '0')

export function isoToDisplay(iso: string | null | undefined): string {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return ''
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

/** Parses dd/MM/yyyy into yyyy-MM-dd; null when not a real calendar date. */
export function displayToIso(text: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text)
  if (!m) return null
  const day = Number(m[1]), month = Number(m[2]), year = Number(m[3])
  if (year < 1900 || year > 2200) return null
  const dt = new Date(year, month - 1, day)
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) return null
  return `${year}-${pad2(month)}-${pad2(day)}`
}

/** Keeps digits only and re-inserts the slashes: 1 → "1", 12 → "12", 120 → "12/0", 12012026 → "12/01/2026". */
export function maskDate(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8)
  let out = digits.slice(0, 2)
  if (digits.length > 2) out += '/' + digits.slice(2, 4)
  if (digits.length > 4) out += '/' + digits.slice(4, 8)
  return out
}

export default function DateInput({ value, onChange, min, max, required, disabled, placeholder, id, name, className, style, 'aria-label': ariaLabel }: DateInputProps) {
  const { t } = useTranslation()
  const [text, setText] = useState(() => isoToDisplay(value))
  const [invalid, setInvalid] = useState(false)
  const nativeRef = useRef<HTMLInputElement>(null)
  const last = useRef(value)

  // Sync from the outside only when the parent actually changed the value (not as an echo of our own emit),
  // so a half-typed date is never clobbered.
  useEffect(() => {
    if (value !== last.current) {
      last.current = value
      setText(isoToDisplay(value))
      setInvalid(false)
    }
  }, [value])

  const emit = (iso: string) => {
    if (iso !== last.current) {
      last.current = iso
      onChange(iso)
    }
  }
  const inRange = (iso: string) => (!min || iso >= min.slice(0, 10)) && (!max || iso <= max.slice(0, 10))

  const handleText = (raw: string) => {
    const masked = maskDate(raw)
    setText(masked)
    if (masked === '') { setInvalid(false); emit(''); return }
    if (masked.length < 10) { setInvalid(false); return }
    const iso = displayToIso(masked)
    if (iso && inRange(iso)) { setInvalid(false); emit(iso) } else { setInvalid(true); emit('') }
  }
  const handleBlur = () => {
    if (text !== '' && (text.length < 10 || displayToIso(text) === null)) { setInvalid(true); emit('') }
  }
  const openPicker = () => {
    const el = nativeRef.current
    if (!el || disabled) return
    const picker = el as HTMLInputElement & { showPicker?: () => void }
    if (typeof picker.showPicker === 'function') {
      try { picker.showPicker(); return } catch { /* fall through to focus */ }
    }
    el.focus()
    el.click()
  }
  const onNative = (iso: string) => {
    setText(isoToDisplay(iso))
    setInvalid(false)
    emit(iso)
  }

  return (
    <span className={`date-input ${invalid ? 'invalid' : ''} ${className ?? ''}`} style={style}>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        id={id}
        name={name}
        size={12}
        maxLength={10}
        value={text}
        placeholder={placeholder ?? t('dateInput.placeholder')}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        required={required}
        disabled={disabled}
        onChange={(e) => handleText(e.target.value)}
        onBlur={handleBlur}
      />
      <button type="button" className="secondary date-pick" onClick={openPicker} disabled={disabled} title={t('dateInput.pick')} aria-label={t('dateInput.pick')}>📅</button>
      <input
        ref={nativeRef}
        type="date"
        className="date-native"
        tabIndex={-1}
        aria-hidden="true"
        value={/^\d{4}-\d{2}-\d{2}$/.test(value) ? value : ''}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(e) => onNative(e.target.value)}
      />
    </span>
  )
}
